import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { Play, CalendarClock, X, Mic } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite } from "@/lib/site";

const ytId = (v) => {
  if (!v) return "";
  const m = String(v).match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/|live\/)([\w-]{11})/);
  return m ? m[1] : /^[\w-]{11}$/.test(v) ? v : "";
};

const thumb = (p) =>
  p.thumbnail || (ytId(p.youtube_id) ? `https://i.ytimg.com/vi/${ytId(p.youtube_id)}/hqdefault.jpg` : "/assets/yogyata-banner.jpg");

const Card = ({ p, i, onPlay }) => (
  <Reveal delay={i * 0.1}>
    <article
      data-testid={`podcast-card-${i}`}
      className="group overflow-hidden rounded-3xl border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-deep"
    >
      <button
        data-testid={`podcast-play-${i}`}
        onClick={() => {
          if (!ytId(p.youtube_id)) return;
          window.__tblTrack?.("podcast_play", { title: p.title });
          onPlay(p);
        }}
        className="relative block w-full text-left"
      >
        <img src={thumb(p)} alt={p.title} className="aspect-video w-full object-cover" loading="lazy" />
        {p.upcoming ? (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-terra px-3 py-1.5 text-[11px] font-outfit font-semibold uppercase tracking-wider text-white">
            <CalendarClock className="h-3 w-3" />
            {p.release_date ? `Releasing ${dayjs(p.release_date).format("D MMM YYYY")}` : "Coming soon"}
          </span>
        ) : (
          ytId(p.youtube_id) && (
            <span className="absolute inset-0 flex items-center justify-center bg-ink/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-terra text-white shadow-deep">
                <Play className="h-6 w-6 fill-white" />
              </span>
            </span>
          )
        )}
      </button>
      <div className="p-6">
        <p className="flex items-center gap-1.5 font-outfit text-xs font-semibold uppercase tracking-widest text-terra">
          <Mic className="h-3.5 w-3.5" /> {p.show || "Podcast"}
        </p>
        <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-ink">{p.title}</h3>
        {p.description && <p className="mt-2 text-sm leading-relaxed text-plumsoft">{p.description}</p>}
      </div>
    </article>
  </Reveal>
);

export const Podcasts = () => {
  const { content } = useSite();
  const [playing, setPlaying] = useState(null);
  const all = content.podcasts || [];
  const published = all.filter((p) => !p.upcoming);
  const upcoming = all.filter((p) => p.upcoming);

  return (
    <section id="podcasts" data-testid="podcasts-section" className="bg-cream/60 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>Podcasts & media</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink">
            Conversations that <span className="italic text-terra">travel far.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {published.map((p, i) => (
            <Card key={p.id || i} p={p} i={i} onPlay={setPlaying} />
          ))}
        </div>

        {upcoming.length > 0 && (
          <>
            <Reveal className="mt-16">
              <p className="font-outfit text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-sage">
                Upcoming episodes
              </p>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="upcoming-podcasts">
              {upcoming.map((p, i) => (
                <Card key={p.id || i} p={p} i={i} onPlay={setPlaying} />
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {playing && (
          <motion.div
            data-testid="podcast-player-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
            onClick={() => setPlaying(null)}
          >
            <motion.div
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-black shadow-deep"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="podcast-player-close"
                onClick={() => setPlaying(null)}
                className="absolute -top-1 right-2 z-10 p-2 text-white/80 hover:text-white"
                aria-label="Close player"
              >
                <X className="h-6 w-6" />
              </button>
              <iframe
                src={`https://www.youtube.com/embed/${ytId(playing.youtube_id)}?autoplay=1`}
                title={playing.title}
                className="aspect-video w-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
